// cspell:ignore unprovisioned
/**
 * Artwork storage for the watermark library (VE-24693 §K).
 *
 * An image watermark's artwork does NOT live on its SDO row. Creating one
 * creates a TDO of its own, carrying two assets:
 *
 *   `watermark-source-image`  the upload's own bytes, untouched — what Download
 *                             hands back, and the only form that cannot be
 *                             reconstructed. Absent for an SVG upload (SEC-R2:
 *                             this app does not sanitize SVG, so it is not
 *                             stored or redistributed).
 *   `watermark-raster`        our re-encoded PNG — the working copy. Copied into
 *                             a document's own asset when the watermark is
 *                             applied (§J), which is what keeps an applied mark
 *                             independent of the library entry forever after.
 *
 * The SDO row keeps `imageTdoId` to find them and a small inline
 * `previewDataUrl` for every surface that only LISTS watermarks. That split is
 * the point: the library list is fetched on three separate route entries, and
 * it now costs kilobytes a row instead of carrying full artwork every time.
 *
 * ## The container TDO must stay invisible
 *
 * Redact lists TDOs in exactly two ways, and a watermark's container must miss
 * both:
 *
 *  1. As a folder's children (`folder.childTDOs`) — so it is created with NO
 *     `parentFolderId`. Do not add one.
 *  2. As "Unassigned Media", whose membership is the EXISTENCE of a redact-state
 *     SDO per TDO (`config.tdoRedactFileStatusRegistryId`), not absence from a folder —
 *     see `casemedia/unassigned.ts`. So no redact-state SDO is ever created for
 *     it. Do not add one.
 *
 * There is no org-wide `temporalDataObjects` query in this app, so those two are
 * the whole exposure. Add either and watermark containers start appearing in the
 * dashboard as files.
 *
 * ## Ordering
 *
 * Create: TDO and its assets FIRST, then the SDO row. A failed SDO write leaves
 * an orphan TDO — invisible junk, and nothing a user can see is broken.
 *
 * Permanent delete: the SDO row FIRST, then the TDO. A failed TDO delete leaks
 * storage; the other order would leave a live row pointing at artwork that is
 * gone. Neither is swept, so both are logged.
 */
import { fetchGraphQLApi } from '@helpers/callApi';

/** The upload's own bytes. Download reads this; nothing renders it. */
export const WATERMARK_SOURCE_IMAGE_ASSET_TYPE = 'watermark-source-image';

/** Our re-encoded PNG — the working copy every consumer draws from. */
export const WATERMARK_RASTER_ASSET_TYPE = 'watermark-raster';

/**
 * REVERT SWITCH — also keep the full raster inline on the SDO row.
 *
 * Off. The whole of §K is that artwork moves to a TDO; this exists so that a
 * cluster where asset storage misbehaves can be put back on the old shape by
 * flipping one constant and redeploying, without reverting the feature.
 *
 * When on, a create/edit writes `imageBaseDataUrl` alongside `imageTdoId`, and
 * because the read path already prefers an inline raster when one is present
 * (see `resolveWatermarkRaster`), those rows stop needing the asset fetch
 * entirely. Rows written while it was off keep working either way — they resolve
 * through the TDO.
 *
 * The key it writes is no longer a property of the published schema, which is a
 * caveat rather than a blocker: `additionalProperties` is open, so the value is
 * still accepted and still read back, but it goes in UNVALIDATED. Restoring the
 * property is therefore part of turning this on for real — the snippet to paste
 * back is in `docs/schemas/WatermarkLibrarySchema.md` under "Constraints left
 * out on purpose". This path is more likely to be deleted than revived.
 *
 * It does NOT bring back inline `sourceDataUrl`. The original upload is what
 * doubled the row, it is read by one feature (Download), and the asset is a
 * strictly better home for it.
 */
export const STORE_WATERMARK_RASTER_IN_SDO = false;

export interface WatermarkAssetConfig {
  token: string;
  apiRoot: string;
  graphQLEndpoint: string;
  veritoneAppId: string;
}

interface CreateTdoResponse {
  createTDO?: { id?: string };
}

interface CreateAssetResponse {
  createAsset?: { id?: string; container?: { id?: string } | null };
}

interface AssetsByTypeResponse {
  temporalDataObject?: {
    id?: string;
    assets?: {
      records?: Array<{
        id: string;
        signedUri: string;
        contentType?: string | null;
        createdDateTime?: string;
      }>;
    } | null;
  } | null;
}

/**
 * Create the container TDO for one watermark's artwork.
 *
 * `startDateTime`/`stopDateTime` are required by `CreateTDO` and carry no
 * meaning here — a watermark has no timeline — so both are stamped now.
 *
 * Deliberately NO `parentFolderId`: see the header. `name` exists only to make
 * the record legible to anyone who finds it through the API.
 */
export async function serviceCreateWatermarkTdo(
  watermarkName: string,
  cfg: WatermarkAssetConfig
): Promise<string> {
  const now = new Date().toISOString();
  const query = `
    mutation createWatermarkTdo($input: CreateTDO) {
      createTDO(input: $input) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<CreateTdoResponse>({
    endpoint: `${cfg.apiRoot}/${cfg.graphQLEndpoint}`,
    query,
    variables: {
      input: {
        startDateTime: now,
        stopDateTime: now,
        name: `Watermark: ${watermarkName}`.slice(0, 255),
        details: { redactWatermarkContainer: true },
      },
    },
    token: cfg.token,
    veritoneAppId: cfg.veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `watermark TDO create failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const id = result.data?.createTDO?.id;
  if (!id) {
    throw new Error('watermark TDO create returned no id');
  }
  return id;
}

/**
 * Upload one artwork asset onto a watermark's TDO.
 *
 * Multipart, like every other asset write in this app — `createAsset` takes the
 * file as a form field, not as a GraphQL variable.
 *
 * `contentType` is taken from the data URL rather than assumed: the raster is
 * always our PNG, but the source keeps whatever was uploaded, and an asset that
 * claims the wrong type downloads with the wrong extension.
 */
export async function serviceSaveWatermarkAsset(
  tdoId: string,
  assetType: string,
  dataUrl: string,
  fileName: string,
  cfg: WatermarkAssetConfig
): Promise<string> {
  const { blob, contentType } = dataUrlToBlob(dataUrl);
  const query = `
    mutation saveWatermarkAsset($id: ID!, $type: String!, $contentType: String!) {
      createAsset(input: {
        containerId: $id,
        type: $type,
        contentType: $contentType
      }) {
        id
        container { id }
      }
    }
  `;
  const formData = new FormData();
  formData.append('query', query);
  formData.append(
    'variables',
    JSON.stringify({ id: tdoId, type: assetType, contentType })
  );
  formData.append('filename', fileName);
  formData.append('file', blob, fileName);

  const res = await fetch(`${cfg.apiRoot}/${cfg.graphQLEndpoint}`, {
    method: 'POST',
    body: formData,
    headers: cfg.token ? { Authorization: `Bearer ${cfg.token}` } : undefined,
  });
  if (!res.ok) {
    throw new Error(`${assetType} save failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: CreateAssetResponse;
    errors?: Array<{ message?: string }>;
  };
  if (json.errors?.length) {
    throw new Error(
      `${assetType} save failed: ${json.errors[0]?.message ?? 'unknown error'}`
    );
  }
  const id = json.data?.createAsset?.id;
  if (!id) {
    throw new Error(`${assetType} save returned no asset`);
  }
  return id;
}

/**
 * Fetch the NEWEST asset of `assetType` from a watermark's TDO, as a data URL.
 *
 * A data URL rather than the signed URI, for the same reason the document-side
 * loader does it (`documents/services.ts`): the drawn form is derived with
 * `canvas.getImageData`, which taints on a cross-origin image and then throws.
 * Reading the bytes here and re-inlining them keeps every downstream consumer
 * working on a same-origin value.
 *
 * Newest rather than only: an edit that replaces the artwork adds an asset
 * rather than mutating one, so a TDO can legitimately hold several. The list is
 * ordered by the API; the first record is taken.
 *
 * Returns null when the TDO holds no asset of that type — a definition saved
 * before §K, or an SVG upload, which stores no source. Callers fall back.
 */
export async function serviceLoadWatermarkAsset(
  tdoId: string,
  assetType: string,
  cfg: WatermarkAssetConfig
): Promise<string | null> {
  // `assetType` / `orderBy` / `orderDirection`, matching every other asset read
  // in this app (`documents/services.ts`). The type is interpolated rather than
  // passed as a variable for the same reason it is there: the argument is a
  // plain String on the schema and the value is one of this module's own
  // constants, never user input.
  const query = `
    query loadWatermarkAsset($id: ID!) {
      temporalDataObject(id: $id) {
        id
        assets(assetType: "${assetType}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records {
            id
            signedUri
            contentType
            createdDateTime
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<AssetsByTypeResponse>({
    endpoint: `${cfg.apiRoot}/${cfg.graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token: cfg.token,
    veritoneAppId: cfg.veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `${assetType} load failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  const record = result.data?.temporalDataObject?.assets?.records?.[0];
  if (!record?.signedUri) {
    return null;
  }
  const res = await fetch(record.signedUri);
  if (!res.ok) {
    throw new Error(`${assetType} fetch failed: HTTP ${res.status}`);
  }
  const blob = await res.blob();
  return await blobToDataUrl(blob);
}

/**
 * Delete a watermark's container TDO, and with it both artwork assets.
 *
 * Called only from the permanent delete, AFTER the SDO row is gone. Documents
 * that already applied the watermark are unaffected: each holds its own raster
 * asset on its own TDO (§J), which is the property that makes this safe.
 */
export async function serviceDeleteWatermarkTdo(
  tdoId: string,
  cfg: WatermarkAssetConfig
): Promise<void> {
  const query = `
    mutation deleteWatermarkTdo($id: ID!) {
      deleteTDO(id: $id) {
        id
        message
      }
    }
  `;
  const result = await fetchGraphQLApi<{ deleteTDO?: { id?: string } }>({
    endpoint: `${cfg.apiRoot}/${cfg.graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token: cfg.token,
    veritoneAppId: cfg.veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `watermark TDO delete failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
}

/**
 * Decode any accepted data URL to a Blob plus the media type it declared.
 *
 * Broader than the document side's PNG-only helper, because the SOURCE asset
 * keeps whatever was uploaded. The allowlist is still enforced — this app only
 * ever produces these three, so anything else is a caller bug and throwing says
 * so rather than posting it to the platform.
 */
function dataUrlToBlob(dataUrl: string): { blob: Blob; contentType: string } {
  const match =
    /^data:(image\/(?:png|jpeg|svg\+xml));base64,([A-Za-z0-9+/]+={0,2})$/.exec(
      dataUrl
    );
  if (!match?.[1] || !match[2]) {
    throw new Error('Watermark artwork is not an accepted inline data URL');
  }
  const contentType = match[1];
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return { blob: new Blob([bytes], { type: contentType }), contentType };
}

/**
 * Read fetched asset bytes back as a data URL.
 *
 * `readAsDataURL` always yields a string, but `FileReader.result` is typed
 * `string | ArrayBuffer | null` because the same field serves
 * `readAsArrayBuffer`. Narrowed rather than stringified: `String(buffer)` would
 * quietly produce `"[object ArrayBuffer]"` and hand it on as an image source.
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read watermark asset'));
    reader.onload = () => {
      const { result } = reader;
      if (typeof result === 'string') {
        resolve(result);
        return;
      }
      reject(new Error('Watermark asset did not read as a data URL'));
    };
    reader.readAsDataURL(blob);
  });
}
